import React, { Suspense, useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Grid,
  Button,
} from "@mui/material";
import {
  ArrowBack,
  DirectionsBus,
  Assignment,
  LocalShipping,
  Luggage,
  Star,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { TripProvider, useTripContext } from "./context/TripContext";
import { TripData, TabConfig, TripBasicData } from "./types";
import tripApi from "./tripApis";
import { formatDate } from "../../../utils/utils";
import NavigationHeader from "../../../components/NavigationHeader";
import { LuggageBookingProvider } from "./context/LuggageBookingContext";
import BasicDetailsEditModal from "./components/BasicDetailsEditModal";
import BasicChangeLogs from "./components/BasicChangeLogs";

import { usePlanStatus } from "../../../hooks/usePlanStatus";

// Lazy load components
const VehicleSelection = React.lazy(
  () => import("./components/VehicleSelection")
);
const IncomeDetails = React.lazy(() => import("./components/IncomeDetails"));
const LuggageDetails = React.lazy(() => import("./components/LuggageDetails"));
const PickupLuggageDetails = React.lazy(
  () => import("./components/PickupLuggageDetails")
);
const ExpenseDetails = React.lazy(() => import("./components/ExpenseDetails"));
const RatingsReviews = React.lazy(() => import("./components/RatingsReviews"));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trip-tabpanel-${index}`}
      aria-labelledby={`trip-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2 }}>
          <Suspense fallback={<Loading />}>{children}</Suspense>
        </Box>
      )}
    </div>
  );
}

export const tabsConfig: TabConfig[] = [
  {
    label: "Bus Income Details",
    icon: <DirectionsBus sx={{ fontSize: 16 }} />,
    path: "bus-income-details",
    component: IncomeDetails,
    description: "Bus booking income details",
  },
  {
    label: "Luggage Income Details",
    icon: <Luggage sx={{ fontSize: 16 }} />,
    path: "luggage-income-details",
    component: LuggageDetails,
    description: "Luggage booking details",
  },
  {
    label: "Pickup Man Luggage Details",
    icon: <LocalShipping sx={{ fontSize: 16 }} />,
    path: "pickup-luggage-details",
    component: PickupLuggageDetails,
    description: "Pickup luggage details",
  },
  {
    label: "Expense",
    icon: <Assignment sx={{ fontSize: 16 }} />,
    path: "expense",
    component: ExpenseDetails,
    description: "Expense details",
  },
  {
    label: "Ratings & Reviews",
    icon: <Star sx={{ fontSize: 16 }} />,
    path: "ratings-reviews",
    component: RatingsReviews,
    description: "Ratings & Reviews",
  },
];

const TripFormContent = () => {
  const navigate = useNavigate();
  const { tripId, section } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { tripData, updateTripData } = useTripContext();

  const [currentTab, setCurrentTab] = useState(0);
  const [showTabs, setShowTabs] = useState(Boolean(tripId));

  // Completion status for each tab
  const [tabCompletionStatus, setTabCompletionStatus] = useState<{
    [key: number]: boolean;
  }>({
    0: false, // Income
    1: false, // Luggage
    2: false, // Pickup
    3: false, // Summary
  });

  // Set initial tab based on section param
  useEffect(() => {
    if (section) {
      const tabIndex = tabsConfig.findIndex((tab) => tab.path === section);
      if (tabIndex !== -1) {
        setCurrentTab(tabIndex);
      }
    }
  }, [section]);

  const fetchTripData = async () => {
    try {
      setIsLoading(true);
      if (tripId) {
        const trip = await tripApi.getTrip(tripId);
        if (trip) {
          updateTripData("tripId", trip.trip.id);
          updateTripData("tripBasic", trip.trip);
          updateTripData("previousTrip", trip.previousTrip);
          updateTripData("vehicle", trip.trip.vehicle);
          updateTripData("route", trip.trip.route);
        }
      }
    } catch (error) {
      console.error("Error fetching trip data:", error);
      showToast("Failed to fetch trip data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTripData();
  }, [tripId]);

  const canNavigateToTab = (tabIndex: number) => {
    return true;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (!canNavigateToTab(newValue)) {
      return;
    }

    setCurrentTab(newValue);
    const tabPath = tabsConfig[newValue].path;
    navigate(`/trip/daily-bus/${tripId ? tripId + "/" : ""}${tabPath}`, {
      replace: true,
    });
  };

  const handleVehicleRouteNext = () => {
    setShowTabs(true);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, mb: 2 }}>
      {/* Header Section */}
      <NavigationHeader
        title="Daily Bus Trip Form"
        breadcrumbs={[
          { label: "Trips", path: "/trips" },
          { label: "Daily Bus Trip", path: "" },
        ]}
      />

      {/* Selected Vehicle Summary - Show when vehicle is selected */}
      {tripData.vehicle && tripData.tripBasic && showTabs && (
        <BasicTripDetails tripData={tripData} fetchTripData={fetchTripData} />
      )}

      {/* Main Form Container */}
      {!showTabs ? (
        // Show Vehicle Selection for new trips
        <Box sx={{ p: 0 }}>
          <Suspense fallback={<Loading />}>
            <VehicleSelection
              onNext={handleVehicleRouteNext}
              isNew={!Boolean(tripId)}
            />
          </Suspense>
        </Box>
      ) : (
        // Show Tabs after vehicle selection or for existing trips
        <>
          {/* Tabs */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              sx={{
                backgroundColor: "#FFFFFF",
                minHeight: 42,
                "& .MuiTab-root": {
                  minHeight: 42,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  textTransform: "none",
                  px: isMobile ? 1 : 2,
                  py: 0.5,
                  gap: 0.5,
                  opacity: 1,
                },
                "& .MuiTabs-indicator": {
                  height: 3,
                },
                border: "1px solid #E0E0E0",
                borderRadius: "4px",
              }}
            >
              {tabsConfig.map((tab, index) => {
                const isCompleted = tabCompletionStatus[index];

                return (
                  <Tab
                    key={tab.path}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {tab.label}
                        {isCompleted && (
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: "success.main",
                            }}
                          />
                        )}
                      </Box>
                    }
                    icon={tab.icon}
                    iconPosition="start"
                    sx={{
                      "& .MuiTab-iconWrapper": {
                        color: isCompleted ? "success.main" : "inherit",
                      },
                    }}
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ minHeight: "50vh" }}>
            {tabsConfig.map((tab, index) => (
              <TabPanel key={tab.path} value={currentTab} index={index}>
                <tab.component
                  onNext={() => {
                    if (index >= tabsConfig.length - 1) {
                      navigate(`/trips`);
                    } else {
                      const nextTab = Math.min(
                        index + 1,
                        tabsConfig.length - 1
                      );
                      if (canNavigateToTab(nextTab)) {
                        handleTabChange(null as any, nextTab);
                      }
                    }
                  }}
                  onPrevious={() => {
                    const prevTab = Math.max(index - 1, 0);
                    handleTabChange(null as any, prevTab);
                  }}
                  isFirst={index === 0}
                  isLast={index === tabsConfig.length - 1}
                  currentStep={index}
                  totalSteps={tabsConfig.length}
                  tabConfig={tab}
                />
              </TabPanel>
            ))}
          </Box>
        </>
      )}
    </Container>
  );
};

const TripForm = () => {
  return (
    <TripProvider>
      <LuggageBookingProvider>
        <TripFormContent />
      </LuggageBookingProvider>
    </TripProvider>
  );
};

export default TripForm;

const BasicTripDetails = ({
  tripData,
  fetchTripData,
}: {
  tripData: TripData;
  fetchTripData: () => void;
}) => {
  const vehicle = tripData.vehicle;
  const tripBasic = tripData.tripBasic;
  const [open, setOpen] = useState(false);
  const [openChangeLogs, setOpenChangeLogs] = useState(false);

  const { planStatus } = usePlanStatus();
  const { canEditBasic } = useMemo(() => {
    return {
      canEditBasic: planStatus?.permissions?.canEditTripBasic || false,
    };
  }, [planStatus?.permissions]);

  return (
    <Card
      sx={{
        mb: 1,
        borderLeft: "4px solid",
        borderLeftColor: "primary.main",
      }}
    >
      <CardContent sx={{ py: 1, "&:last-child": { pb: 1.5 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={10}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DirectionsBus sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "primary.main",
                  display: "flex",
                  flexDirection: "column",
                  fontSize: "0.75rem",
                }}
              >
                {vehicle?.vehicleNumber}
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    opacity: 0.8,
                    fontSize: "0.65rem",
                  }}
                >
                  {vehicle?.vehicleType} • {vehicle?.acquisitionType}
                </Typography>
              </Typography>
              {tripBasic && (
                <>
                  <Box
                    sx={{
                      ml: 1,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 0.5, sm: 1 },
                      alignItems: { xs: "flex-start", sm: "center" },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                      <strong style={{ fontSize: "0.75rem" }}>Route:</strong>{" "}
                      {`${tripBasic.fromLocation} ${
                        tripBasic.tripType === "Round" ? "↔" : "→"
                      } ${tripBasic.toLocation}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        display: { xs: "block", sm: "inline" },
                      }}
                    >
                      <strong style={{ fontSize: "0.75rem" }}>Type:</strong>{" "}
                      {tripBasic.tripType} Trip
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        display: { xs: "block", sm: "inline" },
                      }}
                    >
                      <strong style={{ fontSize: "0.75rem" }}>Date:</strong>{" "}
                      {formatDate(tripBasic?.journeyStartDate || "")}
                      {tripBasic?.tripType === "Round" &&
                        ` - ${formatDate(tripBasic?.journeyReturnDate || "")}`}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sm={2}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {canEditBasic && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setOpen(true)}
                title="Edit Trip Details"
                sx={{
                  py: 0.25,
                  px: 0.5,
                  minWidth: "auto",
                  fontSize: "0.75rem",
                  mr: 1,
                }}
              >
                Edit
              </Button>
            )}
            {tripBasic?.changeLogs && tripBasic?.changeLogs?.length > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={() => setOpenChangeLogs(true)}
                title="Change Logs"
                sx={{
                  py: 0.25,
                  px: 0.5,
                  minWidth: "auto",
                  fontSize: "0.75rem",
                  textDecoration: "underline",
                  color: "grey",
                }}
              >
                Change Logs
              </Button>
            )}
          </Grid>
        </Grid>
      </CardContent>
      <BasicDetailsEditModal
        tripBasicData={tripBasic!}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        fetchTripData={fetchTripData}
      />
      <BasicChangeLogs
        open={openChangeLogs}
        onClose={() => setOpenChangeLogs(false)}
        changedDetails={tripBasic!}
      />
    </Card>
  );
};
